import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box, FormControl, FormLabel, Input, Textarea, Button, useToast,
  VStack, Text, useColorModeValue, HStack, Icon, Badge, Select,
  Switch, Divider, Card, CardBody, SimpleGrid, Stat, StatLabel,
  StatNumber, StatHelpText, Alert, AlertIcon, AlertDescription,
  Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, InputGroup, InputLeftElement, InputRightElement,
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
  Tag, Wrap, WrapItem, Tooltip, IconButton, Stack, Checkbox,
  Flex
} from '@chakra-ui/react';
import {
  MdSend, MdSchedule, MdNotifications, MdPreview,
  MdSave, MdClear, MdInfo, MdWarning, MdCheckCircle,
  MdContentCopy, MdCalendarToday,
  MdLocationOn, MdLanguage, MdDevices, MdSettings
} from 'react-icons/md';
import { sendBroadcastToPublic, getNotificationTemplates } from '../../services/notificationService';

// System presets that are always available
const SYSTEM_TEMPLATES = [
  {
    id: 'sys_service_alert',
    name: 'Service Alert (System)',
    title: '⚠️ Service Alert: {area}',
    body: 'We are experiencing service issues in {area}. Estimated restoration: {time}',
    category: 'service',
    priority: 'high'
  },
  {
    id: 'sys_maintenance',
    name: 'Scheduled Maintenance (System)',
    title: '🔧 Scheduled Maintenance',
    body: 'Planned maintenance on {date} from {start} to {end}. {area} will be affected.',
    category: 'maintenance',
    priority: 'normal'
  },
  {
    id: 'sys_emergency',
    name: 'Emergency Notification (System)',
    title: '🚨 Emergency: {type}',
    body: 'Emergency situation reported. {details}. Please avoid {area}.',
    category: 'emergency',
    priority: 'critical'
  }
];

function PublicBroadcastForm() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [targetArea, setTargetArea] = useState('all');
  const [language, setLanguage] = useState('en');
  const [scheduledFor, setScheduledFor] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [includeInApp, setIncludeInApp] = useState(true);
  const [includePush, setIncludePush] = useState(true);
  const [includeSMS, setIncludeSMS] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [estimatedReach, setEstimatedReach] = useState(1250); // Mock number
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  
  // State for all available templates (System + Database)
  const [availableTemplates, setAvailableTemplates] = useState(SYSTEM_TEMPLATES);

  const toast = useToast();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
  const { isOpen: isTemplateOpen, onOpen: onTemplateOpen, onClose: onTemplateClose } = useDisclosure();
  
  const cardBg = useColorModeValue("white", "navy.700");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const accentColor = useColorModeValue("blue.500", "blue.300");

  // Fetch Custom Templates on Mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const dbTemplates = await getNotificationTemplates();
        // Combine system presets with DB templates
        // We map DB 'type' to 'priority' or 'category' as needed
        const formattedDbTemplates = dbTemplates.map(t => ({
           ...t,
           category: 'general', // Default category for DB templates
           priority: mapDbTypeToPriority(t.type)
        }));
        
        setAvailableTemplates([...SYSTEM_TEMPLATES, ...formattedDbTemplates]);
      } catch (error) {
        console.error("Failed to load custom templates", error);
        // Fallback to just system templates if DB fails
      }
    };
    fetchTemplates();
  }, []);

  // Helper to map DB template types (info, warning, critical) to Form priorities
  const mapDbTypeToPriority = (type) => {
      switch(type) {
          case 'critical': return 'critical';
          case 'warning': return 'high';
          case 'success': return 'low';
          default: return 'normal';
      }
  };

  const characterCount = useMemo(() => {
    return {
      title: title.length,
      body: body.length,
      total: title.length + body.length,
      maxTitle: 50,
      maxBody: 200
    };
  }, [title, body]);

  const deliveryChannels = useMemo(() => {
    const channels = [];
    if (includeInApp) channels.push('In-App');
    if (includePush) channels.push('Push');
    if (includeSMS) channels.push('SMS');
    return channels;
  }, [includeInApp, includePush, includeSMS]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setTitle(template.title);
    setBody(template.body);
    
    // If template has a specific category, use it, otherwise default to current
    if (template.category) setCategory(template.category);
    
    // If template has a priority, use it
    if (template.priority) setPriority(template.priority);

    onTemplateClose();
    
    toast({
      title: "Template Applied",
      description: `Using "${template.name}". You can edit the content before sending.`,
      status: "info",
      duration: 2500,
      isClosable: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !body) {
      toast({
        title: "Fields Required",
        description: "Please fill out both the title and body.",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top-right"
      });
      return;
    }

    if (!includePush && !includeInApp && !includeSMS) {
      toast({
        title: "No Delivery Channel",
        description: "Please select at least one delivery channel.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const broadcastData = {
        title,
        body,
        category,
        priority,
        targetArea,
        language,
        channels: {
          inApp: includeInApp,
          push: includePush,
          sms: includeSMS
        }
      };

      if (isScheduled && scheduledFor) {
        broadcastData.scheduledFor = scheduledFor;
      }

      const response = await sendBroadcastToPublic(broadcastData);
      
      toast({
        title: "Broadcast Sent Successfully",
        description: response.message || `Notification queued for delivery.`,
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });

      setBroadcastHistory(prev => [{
        id: Date.now(),
        title,
        body,
        timestamp: new Date().toISOString(),
        recipients: response.totalRecipients || estimatedReach
      }, ...prev.slice(0, 9)]);

      // Reset specific fields but keep settings
      setTitle('');
      setBody('');
      setSelectedTemplate(null);
      
    } catch (error) {
      toast({
        title: "Error Sending Broadcast",
        description: error.message || "An unknown error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setTitle('');
    setBody('');
    setSelectedTemplate(null);
  };

  const handleSaveTemplate = () => {
    // In a real app, this would open the TemplateManager modal with current data
    toast({
      title: "Template Saved",
      description: "Navigate to Template Manager to edit your saved templates.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <VStack spacing={6} align="stretch">
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        <Box flex="1">
          <AlertDescription fontSize="sm">
            This message will be sent as a push notification to all registered public app users.
            Use responsibly for important community updates.
          </AlertDescription>
        </Box>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <Card bg={cardBg}>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">Estimated Reach</StatLabel>
              <StatNumber color={accentColor}>
                {estimatedReach > 0 ? estimatedReach.toLocaleString() : '---'}
              </StatNumber>
              <StatHelpText>Active users</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg}>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">Delivery Channels</StatLabel>
              <StatNumber fontSize="lg">{deliveryChannels.length}</StatNumber>
              <StatHelpText>
                <Wrap mt={1}>
                  {deliveryChannels.map(channel => (
                    <WrapItem key={channel}>
                      <Tag size="sm" colorScheme="blue">{channel}</Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg}>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">Broadcasts Today</StatLabel>
              <StatNumber>{broadcastHistory.length}</StatNumber>
              <StatHelpText>
                <Text 
                  as="span" 
                  fontSize="xs" 
                  color={accentColor} 
                  cursor="pointer"
                  onClick={onHistoryOpen}
                >
                  View history
                </Text>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card bg={cardBg} borderRadius="xl">
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={5} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="lg" fontWeight="600">
                  <Icon as={MdNotifications} mr={2} />
                  Compose Broadcast
                </Text>
                
                <HStack>
                  <Tooltip label="Choose Template">
                    <Button
                      leftIcon={<MdContentCopy />}
                      onClick={onTemplateOpen}
                      variant="ghost"
                      size="sm"
                      colorScheme="brand"
                    >
                        Templates
                    </Button>
                  </Tooltip>
                  
                  <Tooltip label="Save as Template">
                    <IconButton
                      icon={<MdSave />}
                      onClick={handleSaveTemplate}
                      variant="ghost"
                      size="sm"
                      isDisabled={!title || !body}
                    />
                  </Tooltip>
                </HStack>
              </Flex>

              <Divider />

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    <Icon as={MdInfo} mr={1} />
                    Category
                  </FormLabel>
                  <Select 
                    value={category} 
                    onChange={(e) => setCategory(e.target.value)}
                    size="lg"
                  >
                    <option value="general">General</option>
                    <option value="service">Service Alert</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="emergency">Emergency</option>
                    <option value="announcement">Announcement</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    <Icon as={MdWarning} mr={1} />
                    Priority
                  </FormLabel>
                  <Select 
                    value={priority} 
                    onChange={(e) => setPriority(e.target.value)}
                    size="lg"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">
                  Notification Title
                </FormLabel>
                <InputGroup size="lg">
                  <InputLeftElement>
                    <Icon as={MdNotifications} color={mutedColor} />
                  </InputLeftElement>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Water Service Alert"
                    maxLength={characterCount.maxTitle}
                  />
                  <InputRightElement width="4rem">
                    <Text fontSize="xs" color={mutedColor}>
                      {characterCount.title}/{characterCount.maxTitle}
                    </Text>
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">
                  Notification Body (Message)
                </FormLabel>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="e.g., Planned maintenance in the Eastern District tomorrow from 9 AM to 3 PM."
                  rows={4}
                  size="lg"
                  maxLength={characterCount.maxBody}
                />
                <Flex justify="space-between" mt={1}>
                  <Text fontSize="xs" color={mutedColor}>
                    {characterCount.body}/{characterCount.maxBody} characters
                  </Text>
                  {characterCount.body > characterCount.maxBody * 0.9 && (
                    <Badge colorScheme="orange" fontSize="xs">
                      Approaching limit
                    </Badge>
                  )}
                </Flex>
              </FormControl>

              <Accordion allowToggle>
                <AccordionItem border="none">
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="600">
                      <Icon as={MdSettings} mr={2} />
                      Advanced Options
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack spacing={4} align="stretch">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel fontSize="sm">
                            <Icon as={MdLocationOn} mr={1} />
                            Target Area
                          </FormLabel>
                          <Select value={targetArea} onChange={(e) => setTargetArea(e.target.value)}>
                            <option value="all">All Areas</option>
                            <option value="north">North District</option>
                            <option value="south">South District</option>
                            <option value="east">East District</option>
                            <option value="west">West District</option>
                            <option value="downtown">Downtown</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">
                            <Icon as={MdLanguage} mr={1} />
                            Language
                          </FormLabel>
                          <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                          </Select>
                        </FormControl>
                      </SimpleGrid>

                      <Divider />

                      <Box>
                        <Text fontSize="sm" fontWeight="600" mb={3}>
                          <Icon as={MdDevices} mr={1} />
                          Delivery Channels
                        </Text>
                        <Stack spacing={2}>
                          <Checkbox 
                            isChecked={includeInApp} 
                            onChange={(e) => setIncludeInApp(e.target.checked)}
                          >
                            In-App Notification
                          </Checkbox>
                          <Checkbox 
                            isChecked={includePush} 
                            onChange={(e) => setIncludePush(e.target.checked)}
                          >
                            Push Notification
                          </Checkbox>
                          <Checkbox 
                            isChecked={includeSMS} 
                            onChange={(e) => setIncludeSMS(e.target.checked)}
                          >
                            SMS (Premium Feature)
                          </Checkbox>
                        </Stack>
                      </Box>

                      <Divider />

                      <FormControl display="flex" alignItems="center">
                        <FormLabel htmlFor="schedule-broadcast" mb="0" fontSize="sm">
                          <Icon as={MdSchedule} mr={1} />
                          Schedule for later
                        </FormLabel>
                        <Switch
                          id="schedule-broadcast"
                          isChecked={isScheduled}
                          onChange={(e) => setIsScheduled(e.target.checked)}
                        />
                      </FormControl>

                      {isScheduled && (
                        <FormControl>
                          <FormLabel fontSize="sm">
                            <Icon as={MdCalendarToday} mr={1} />
                            Scheduled Date & Time
                          </FormLabel>
                          <Input
                            type="datetime-local"
                            value={scheduledFor}
                            onChange={(e) => setScheduledFor(e.target.value)}
                            size="lg"
                          />
                        </FormControl>
                      )}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>

              <Divider />

              <HStack justify="space-between">
                <HStack>
                  <Button
                    variant="ghost"
                    onClick={handleClear}
                    leftIcon={<MdClear />}
                    isDisabled={!title && !body}
                  >
                    Clear
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={onPreviewOpen}
                    leftIcon={<MdPreview />}
                    isDisabled={!title || !body}
                  >
                    Preview
                  </Button>
                </HStack>

                <Button
                  type="submit"
                  colorScheme="blue"
                  leftIcon={isScheduled ? <MdSchedule /> : <MdSend />}
                  isLoading={isLoading}
                  loadingText="Sending..."
                  size="lg"
                >
                  {isScheduled ? 'Schedule Broadcast' : 'Send Broadcast'}
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>

      {/* Templates Modal */}
      <Modal isOpen={isTemplateOpen} onClose={onTemplateClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Choose a Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              {availableTemplates.length === 0 ? (
                  <Text color="gray.500">No templates found.</Text>
              ) : (
                availableTemplates.map(template => (
                    <Card
                    key={template.id}
                    cursor="pointer"
                    onClick={() => handleTemplateSelect(template)}
                    _hover={{ borderColor: accentColor, transform: 'translateY(-2px)' }}
                    transition="all 0.2s"
                    border="1px"
                    borderColor={borderColor}
                    >
                    <CardBody>
                        <VStack align="start" spacing={2}>
                        <HStack justify="space-between" w="full">
                            <Text fontWeight="600">{template.name}</Text>
                            <HStack>
                                {template.category && <Badge>{template.category}</Badge>}
                                {template.priority && (
                                    <Badge colorScheme={
                                        template.priority === 'critical' ? 'red' : 
                                        template.priority === 'high' ? 'orange' : 'blue'
                                    }>
                                        {template.priority}
                                    </Badge>
                                )}
                            </HStack>
                        </HStack>
                        <Text fontSize="sm" fontWeight="600" color={mutedColor}>
                            {template.title}
                        </Text>
                        <Text fontSize="sm" color={mutedColor} noOfLines={2}>
                            {template.body}
                        </Text>
                        </VStack>
                    </CardBody>
                    </Card>
                ))
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onTemplateClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Broadcast Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box
                p={4}
                borderRadius="lg"
                bg={hoverBg}
                border="1px"
                borderColor={borderColor}
              >
                <HStack mb={2}>
                  <Icon as={MdNotifications} color={accentColor} />
                  <Badge colorScheme={priority === 'critical' ? 'red' : priority === 'high' ? 'orange' : 'blue'}>
                    {priority}
                  </Badge>
                  <Badge>{category}</Badge>
                </HStack>
                <Text fontWeight="600" fontSize="lg" mb={2}>{title}</Text>
                <Text color={mutedColor}>{body}</Text>
                
                <Divider my={3} />
                
                <VStack align="start" spacing={1} fontSize="xs" color={mutedColor}>
                  <HStack>
                    <Icon as={MdLocationOn} />
                    <Text>Area: {targetArea}</Text>
                  </HStack>
                  <HStack>
                    <Icon as={MdDevices} />
                    <Text>Channels: {deliveryChannels.join(', ')}</Text>
                  </HStack>
                  {isScheduled && scheduledFor && (
                    <HStack>
                      <Icon as={MdSchedule} />
                      <Text>Scheduled: {new Date(scheduledFor).toLocaleString()}</Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPreviewClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* History Modal */}
      <Modal isOpen={isHistoryOpen} onClose={onHistoryClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Broadcast History (Today)</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {broadcastHistory.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                No broadcasts sent today.
              </Alert>
            ) : (
              <VStack spacing={3} align="stretch">
                {broadcastHistory.map(item => (
                  <Card key={item.id} size="sm">
                    <CardBody>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="600">{item.title}</Text>
                        <Text fontSize="sm" color={mutedColor} noOfLines={2}>{item.body}</Text>
                        <HStack fontSize="xs" color={mutedColor}>
                          <Icon as={MdCheckCircle} />
                          <Text>{item.recipients} recipients</Text>
                          <Text>•</Text>
                          <Text>{new Date(item.timestamp).toLocaleTimeString()}</Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onHistoryClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default PublicBroadcastForm;